import { useState, useEffect, memo, useCallback, useRef } from "react";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import AnimacionCarga from "./animacion_de_carga";
import FiltrosTarjetas, { aplicarFiltrosTarjetas } from "./filtros_tarjetas";
import TablaGenerica from "./tablas";
import { obtenerUsuarios } from "../../functions/consultas";
import BotonSOS from "./sos/boton_sos";
import {
  actualizarRegistro,
  actualizarMultiple,
  eliminarRegistro,
  crearRegistro,
  consultarSimple,
} from "../../functions/crud";
import { agregarRazonSocial } from "../../functions/gestion";
import Swal from "sweetalert2";
import { crearUsuarioAuth } from "../../functions/auth";
import "bootstrap-icons/font/bootstrap-icons.css";

// Solo la cuenta de sistemas puede asignar el permiso SUPER.
const puedeAsignarSuper = () =>
  (localStorage.getItem("CorreoElectronico") || "").trim().toLowerCase() === "sistemas@coordinarseguridad.com";

const ROLES_OPCIONES = ["ADMINISTRADOR", "SUPER", "RESIDENTE", "AUDITOR", "GUARDA", "CONTADOR"];

const MOTIVO_EXCLUSION_DEFECTO = "Solicito darse de baja de la base de datos mediante el correo de servicio al cliente.";

const esAdministrador = () => {
  const permisos = localStorage.getItem("Permisos") || "";
  return ["ADMINISTRADOR", "SUPER"].includes(permisos);
};

const columnas = [
  { label: "Razón social", key: "razonSocial" },
  { label: "Producto", key: "producto" },
  { label: "Código", key: "CodigoCliente" },
  { label: "Dirección", key: "Direccion" },
  { label: "Nombre completo", key: "NombreCompleto" },
  { label: "Correo electrónico", key: "CorreoElectronico" },
  { label: "Celular", key: "Celular" },
  { label: "Permiso", key: "Permisos" },
  { label: "Tipo Residente", key: "tipo_residente" },
  { label: "Estado", key: "estadoregistro" },
];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const COLUMNAS_PLANTILLA = [
  "NombreCompleto",
  "Documento",
  "Celular",
  "Direccion",
  "CodigoCliente",
  "CorreoElectronico",
  "Password",
  "Permiso",
  "TipoResidente",
];

const URL_REGISTRO_MASIVO = "https://coordiph.herokuapp.com/registro/masivo";
const TAMANO_LOTE_MASIVO = 50;

const TIPOS_RESIDENTE_OPCIONES = [
  "PROPIETARIO/RESIDENTE",
  "PROPIETARIO/NO RESIDENTE",
  "ARRENDATARIO",
  "LOCATARIO",
];

const ListadoUsuarios = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtros, setFiltros] = useState({});

  const capturarDatos = useCallback(async () => {
    try {
      const data = await obtenerUsuarios();

      if (data) {
        const datosTransformados = Object.keys(data).map((key) => ({
          ...data[key],
          razonSocial: agregarRazonSocial(data[key].CodigoCliente, "nombre"),
          producto: agregarRazonSocial(data[key].CodigoCliente, "producto"),
        }));

        setDatos(datosTransformados);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    capturarDatos();
  }, [capturarDatos]);

  const limpiarRegistrosCorreo = async (tipo, codigo, correo) => {
    await eliminarRegistro(`${tipo}/${codigo}/${correo}`);

    const data = await consultarSimple(`${tipo}/${codigo}`);
    if (!data || typeof data !== "object") return;

    for (const [key, valor] of Object.entries(data)) {
      if (valor && typeof valor === "object" && String(valor.ruta || "").includes(correo)) {
        await eliminarRegistro(`${tipo}/${codigo}/${key}`);
      }
    }
  };

  const notificarAutorizacion = async (fila, codigo) => {
    if (fila.Notificado === true) return;
    if (!fila.idConjunto) {
      console.warn("⚠️ Usuario sin idConjunto, no se envía correo de autorización");
      return;
    }

    try {
      await fetch("https://coordiph.herokuapp.com/registro/notificarautorizacion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mail: fila.CorreoElectronico,
          idCliente: fila.idConjunto,
          CodigoCliente: codigo,
        }),
      });
    } catch (e) {
      console.error("❌ Error enviando correo de autorización:", e);
    }
  };

  const limpiarUsuariosDuplicados = async (correo, idActual) => {
    const data = await consultarSimple(`usuarios/${correo}`);
    if (!data || typeof data !== "object") return;

    for (const key of Object.keys(data)) {
      if (key.includes("-") && key !== idActual) {
        await eliminarRegistro(`usuarios/${correo}/${key}`);
      }
    }
  };

  const aprobarUsuarioWeb = async (fila, estado) => {
    try {
      const codigo = fila.CodigoCliente;
      const correo = fila.CorreoElectronico.replaceAll("@", "").replaceAll(
        ".",
        ""
      );

      await limpiarUsuariosDuplicados(correo, fila.id);
      await limpiarRegistrosCorreo("pendientes", codigo, correo);
      await limpiarRegistrosCorreo("autorizados", codigo, correo);

      await actualizarRegistro(
        `usuarios/${correo}/${fila.id}/estadoregistro`,
        estado
      );
      await actualizarRegistro(`usuarios/${correo}/${fila.id}/Id`, fila.id);
      await actualizarRegistro(
        `usuarios/${correo}/${fila.id}/autorizoUsuario`,
        localStorage.getItem("CorreoElectronico") || ""
      );

      if (estado === "AUTORIZADO") {
        await notificarAutorizacion(fila, codigo);
        await actualizarRegistro(
          `usuarios/${correo}/${fila.id}/Notificado`,
          true
        );
        await crearRegistro(`autorizados/${codigo}`, {
          ruta: `usuarios/${correo}/${fila.id}`,
        });
        await actualizarRegistro(`correos/${correo}`, fila.id);
      } else {
        // La administración solo ve a los usuarios enlazados en autorizados/ o
        // pendientes/ de su NIT: si no queda en ninguno, desaparece de su listado.
        await actualizarRegistro(`pendientes/${codigo}/${correo}`, {
          ruta: `usuarios/${correo}/${fila.id}`,
        });
      }

      console.log("✅ Estado actualizado correctamente");
      return true;
    } catch (e) {
      console.error("❌ Error cambiando estado:", e);
      return false;
    }
  };

  const generarFormularioCrearUsuario = () => `
    <style>
      .swal2-input, .swal2-select {
        margin: 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
        padding: 10px !important;
        border: 1px solid #d1d5db !important;
        border-radius: 5px !important;
        font-size: 14px !important;
      }
      .row-container {
        display: flex;
        gap: 10px;
        margin-bottom: 15px;
      }
      .row-container > div {
        flex: 1;
      }
    </style>
    <div style="text-align:left;padding:20px;">
      <h3 style="margin:0 0 25px;color:#2c3e50;font-size:20px;font-weight:600;">
        Nuevo Usuario
      </h3>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Nombre Completo *</label>
        <input id="nombreCompleto" class="swal2-input" placeholder="Nombre completo del usuario">
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Documento (opcional)</label>
        <input id="documento" type="number" class="swal2-input" placeholder="Número de documento">
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Número de Celular *</label>
        <input id="celular" type="number" class="swal2-input" placeholder="Número de celular">
      </div>
      
      <div class="row-container">
        <div>
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Tipo de Vivienda</label>
          <select id="tipoVivienda" class="swal2-select">
            <option value="APARTAMENTO">APARTAMENTO</option>
            <option value="CASA">CASA</option>
          </select>
        </div>
        <div>
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">No Torre/Etapa</label>
          <input id="torre" class="swal2-input" placeholder="Ej: 1, A, B">
        </div>
        <div>
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">No Casa/Apto</label>
          <input id="numeroCasa" class="swal2-input" placeholder="Ej: 101">
        </div>
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Dirección * <small style="color:#999;">(editable)</small></label>
        <input id="direccion" class="swal2-input" placeholder="Ej: PORTERÍA, ADMINISTRACIÓN, TORRE 1 APTO 101">
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">NIT del Cliente *</label>
        <input id="codigoCliente" type="number" class="swal2-input" placeholder="Código suministrado por la administración">
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Correo Electrónico *</label>
        <input id="correoElectronico" type="email" name="correo-nuevo-usuario" autocomplete="off" class="swal2-input" placeholder="ejemplo@correo.com">
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Contraseña *</label>
        <div style="position:relative;">
          <input id="password" type="password" name="clave-nuevo-usuario" autocomplete="new-password" class="swal2-input" placeholder="Mínimo 6 caracteres" style="padding-right:48px;">
          <button type="button" class="ver-clave" data-target="password" title="Mostrar contraseña" aria-label="Mostrar contraseña" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;font-size:22px;color:#666;padding:6px;"><i class="bi bi-eye"></i></button>
        </div>
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Confirmar Contraseña *</label>
        <div style="position:relative;">
          <input id="password2" type="password" name="clave2-nuevo-usuario" autocomplete="new-password" class="swal2-input" placeholder="Repita la contraseña" style="padding-right:48px;">
          <button type="button" class="ver-clave" data-target="password2" title="Mostrar contraseña" aria-label="Mostrar contraseña" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;font-size:22px;color:#666;padding:6px;"><i class="bi bi-eye"></i></button>
        </div>
      </div>
      
      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Permiso</label>
        <select id="permiso" class="swal2-select">
          <option value="RESIDENTE">RESIDENTE</option>
          <option value="ADMINISTRADOR">ADMINISTRADOR</option>
          <option value="GUARDA">GUARDA</option>
          <option value="AUDITOR">AUDITOR</option>
          <option value="CONTADOR">CONTADOR</option>
          ${puedeAsignarSuper() ? '<option value="SUPER">SUPER</option>' : ""}
        </select>
      </div>

      <div style="margin-bottom:15px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Tipo de Residente</label>
        <select id="tipoResidente" class="swal2-select">
          <option value="">-- Seleccionar --</option>
          ${TIPOS_RESIDENTE_OPCIONES.map((t) => `<option value="${t}">${t}</option>`).join("")}
        </select>
      </div>
    </div>
  `;

  const actualizarDireccion = () => {
    const tipoVivienda = document.getElementById("tipoVivienda")?.value || "APARTAMENTO";
    const torre = document.getElementById("torre")?.value || "";
    const numeroCasa = document.getElementById("numeroCasa")?.value || "";

    if (torre && numeroCasa) {
      const direccion = tipoVivienda === "APARTAMENTO"
        ? `TORRE ${torre} APARTAMENTO ${numeroCasa}`
        : `MANZANA ${torre} CASA ${numeroCasa}`;

      const direccionInput = document.getElementById("direccion");
      if (direccionInput) {
        direccionInput.value = direccion;
      }
    }
  };

  const validarDatosCrearUsuario = () => {
    const nombreCompleto = document.getElementById("nombreCompleto").value.trim();
    const documento = document.getElementById("documento").value.trim() || "0";
    const celular = document.getElementById("celular").value.trim();
    const direccion = document.getElementById("direccion").value.trim();
    const codigoCliente = document.getElementById("codigoCliente").value.trim();
    const correoElectronico = document.getElementById("correoElectronico").value.trim();
    const password = document.getElementById("password").value;
    const password2 = document.getElementById("password2").value;
    const permiso = document.getElementById("permiso").value;
    const tipoResidente = document.getElementById("tipoResidente").value;

    if (!nombreCompleto) {
      Swal.showValidationMessage("El nombre completo es obligatorio");
      return false;
    }

    if (!celular) {
      Swal.showValidationMessage("El número de celular es obligatorio");
      return false;
    }

    if (!direccion || direccion.length < 2) {
      Swal.showValidationMessage("La dirección es obligatoria");
      return false;
    }

    if (!codigoCliente) {
      Swal.showValidationMessage("El código del cliente es obligatorio");
      return false;
    }

    if (!correoElectronico) {
      Swal.showValidationMessage("El correo electrónico es obligatorio");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(correoElectronico)) {
      Swal.showValidationMessage("El correo electrónico no es válido");
      return false;
    }

    if (!password || password.length < 6) {
      Swal.showValidationMessage("La contraseña debe tener al menos 6 caracteres");
      return false;
    }

    if (password !== password2) {
      Swal.showValidationMessage("Las contraseñas no coinciden");
      return false;
    }

    return {
      nombreCompleto,
      documento: documento.length < 4 ? "0" : documento,
      celular,
      direccion,
      codigoCliente,
      correoElectronico: correoElectronico.toLowerCase(),
      password,
      permiso,
      tipoResidente,
    };
  };

  const normalizarClave = (texto) =>
  texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .replace(/[.#$[\]/]/g, "");

  const actualizarCorreosCopropietarios = useCallback(async () => {

    const esSuper = localStorage.getItem("Permisos") === "SUPER";

    const codigoCliente = localStorage.getItem("CodigoCliente");

    const usuariosAutorizados = datos.filter((u) => {
      if (u.estadoregistro !== "AUTORIZADO") return false;
      if (esSuper) return true;
      return String(u.CodigoCliente).trim() === String(codigoCliente || "").trim();
    });

    if (!esSuper && !codigoCliente) return;

    if (usuariosAutorizados.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Sin usuarios",
        text: esSuper
          ? "No se encontraron usuarios autorizados en la tabla."
          : `No se encontraron usuarios autorizados para el código ${codigoCliente}`,
      });
      return;
    }

    const usuariosPorUnidad = usuariosAutorizados.reduce((acc, usuario) => {
      const codigo = String(usuario.CodigoCliente || "").trim();
      if (!codigo) return acc;
      (acc[codigo] = acc[codigo] || []).push(usuario);
      return acc;
    }, {});

    const unidades = Object.keys(usuariosPorUnidad);

    const totalUnidades = unidades.length;

    const renderProgreso = (completadas, codigoActual) => {
      const pct = Math.round((completadas / totalUnidades) * 100);
      const barra = document.getElementById("prog-barra");
      const etiqueta = document.getElementById("prog-etiqueta");
      const detalle = document.getElementById("prog-detalle");
      if (barra) barra.style.width = `${pct}%`;
      if (etiqueta) etiqueta.textContent = `${pct}% — ${completadas}/${totalUnidades} unidades`;
      if (detalle) {
        detalle.textContent = codigoActual
          ? `Procesando unidad ${codigoActual}...`
          : "Finalizando...";
      }
    };

    Swal.fire({
      title: "Actualizando correos",
      html: `
        <div style="text-align:left;padding:5px 5px 0;">
          <p id="prog-detalle" style="margin:0 0 10px;color:#7f8c8d;font-size:13px;">
            Procesando ${usuariosAutorizados.length} usuarios en ${totalUnidades} unidad(es)...
          </p>
          <div style="background:#ecf0f1;border-radius:10px;overflow:hidden;height:18px;">
            <div id="prog-barra" style="width:0%;height:100%;background:#c0392b;transition:width .25s ease;"></div>
          </div>
          <p id="prog-etiqueta" style="margin:8px 0 0;text-align:center;color:#2c3e50;font-size:13px;font-weight:600;">
            0% — 0/${totalUnidades} unidades
          </p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    try {
      let completadas = 0;

      await Promise.all(unidades.map((codigo) => eliminarRegistro(`notificaciones/${codigo}`)));

      for (const codigo of unidades) {
        renderProgreso(completadas, codigo);

        const actualizaciones = {};
        for (const usuario of usuariosPorUnidad[codigo]) {
          const direccionClave = normalizarClave(usuario.Direccion || "sindi reccion");
          const correo = (usuario.CorreoElectronico || "").toLowerCase().trim();

          if (correo) {
            actualizaciones[`notificaciones/${codigo}/${direccionClave}`] = correo;
          }
        }

        await actualizarMultiple(actualizaciones);

        completadas++;
        renderProgreso(completadas, null);
      }

      Swal.fire({
        icon: "success",
        title: "¡Listo!",
        text: `Se actualizaron ${usuariosAutorizados.length} registros en ${unidades.length} unidad(es).`,
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      console.error("Error actualizando correos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo actualizar: " + error.message,
      });
    }
  }, [datos]);

  const crearUsuarioDesdeDatos = async (formValues) => {
    const idConjunto = await consultarSimple(`clientes/${formValues.codigoCliente}`);
    if (!idConjunto || typeof idConjunto === "object") {
      throw new Error("El código del cliente que ingresaste no cuenta con CoordiPH");
    }

    const correoSinFormato = (formValues.correoElectronico || "")
      .trim()
      .replaceAll("@", "")
      .replaceAll(".", "")
      .replaceAll(" ", "");

    if (!correoSinFormato || /[#$[\]/]/.test(correoSinFormato)) {
      throw new Error("Correo electrónico inválido, no se puede registrar");
    }

    const existente = await consultarSimple(`usuarios/${correoSinFormato}`);
    if (existente && Object.keys(existente).length > 0) {
      throw new Error("Ya existe un usuario registrado con este correo");
    }

    await crearUsuarioAuth(formValues.correoElectronico, formValues.password);

    const fechaActual = new Date().toISOString();

    const usuarioData = {
      NombreCompleto: formValues.nombreCompleto,
      Documento: formValues.documento,
      Celular: formValues.celular,
      TelefonoArlterno: "0",
      Direccion: formValues.direccion,
      CodigoCliente: formValues.codigoCliente,
      CorreoElectronico: formValues.correoElectronico,
      Permisos: formValues.permiso,
      tipo_residente: formValues.tipoResidente || "",
      idConjunto,
      estadoregistro: "AUTORIZADO",
      autorizado: true,
      Notificado: true,
      autorizoUsuario: localStorage.getItem("CorreoElectronico") || "",
      fechaCreacion: fechaActual,
      fechaRegistro: fechaActual,
      FotoPerfilUrl: "https://cdn2.iconfinder.com/data/icons/font-awesome/1792/user-512.png"
    };

    const userId = await crearRegistro(`usuarios/${correoSinFormato}`, usuarioData);

    await actualizarRegistro(`usuarios/${correoSinFormato}/${userId}/Id`, userId);

    await crearRegistro(`autorizados/${formValues.codigoCliente}`, {
      ruta: `usuarios/${correoSinFormato}/${userId}`,
    });

    await actualizarRegistro(`correos/${correoSinFormato}`, userId);

    return userId;
  };

  const crearUsuario = useCallback(async (base = null) => {
    const { value: formValues } = await Swal.fire({
      title: base ? "Duplicar Usuario" : "Crear Usuario",
      html: generarFormularioCrearUsuario(),
      showCancelButton: true,
      confirmButtonText: "Crear",
      cancelButtonText: "Cancelar",
      width: 650,
      didOpen: () => {
        Swal.getPopup().querySelectorAll(".ver-clave").forEach((btn) => {
          btn.addEventListener("click", () => {
            const input = document.getElementById(btn.dataset.target);
            const mostrar = input.type === "password";
            input.type = mostrar ? "text" : "password";
            btn.querySelector("i").className = mostrar ? "bi bi-eye-slash" : "bi bi-eye";
            const texto = mostrar ? "Ocultar contraseña" : "Mostrar contraseña";
            btn.title = texto;
            btn.setAttribute("aria-label", texto);
          });
        });
        document.getElementById("documento").value = "0";
        document.getElementById("tipoResidente").value = "PROPIETARIO/RESIDENTE";
        if (base) {
          const valores = {
            nombreCompleto: base.NombreCompleto,
            documento: base.Documento,
            celular: base.Celular,
            direccion: base.Direccion,
            codigoCliente: base.CodigoCliente,
            permiso: base.Permisos,
            tipoResidente: base.tipo_residente,
          };
          Object.entries(valores).forEach(([id, valor]) => {
            const campo = document.getElementById(id);
            if (campo && valor) campo.value = String(valor);
          });
        }
        const tipoVivienda = document.getElementById("tipoVivienda");
        const torre = document.getElementById("torre");
        const numeroCasa = document.getElementById("numeroCasa");

        tipoVivienda?.addEventListener("change", actualizarDireccion);
        torre?.addEventListener("input", actualizarDireccion);
        numeroCasa?.addEventListener("input", actualizarDireccion);
      },
      preConfirm: validarDatosCrearUsuario,
    });

    if (!formValues) return;

    try {
      Swal.fire({
        title: "Creando usuario...",
        text: "Por favor espere",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await crearUsuarioDesdeDatos(formValues);

      Swal.fire({
        icon: "success",
        title: "Usuario creado",
        text: "El usuario ha sido creado correctamente",
        confirmButtonText: "Aceptar",
      });

      capturarDatos();
    } catch (error) {
      console.error("Error al crear usuario:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo crear el usuario: " + error.message,
        confirmButtonText: "Aceptar",
      });
    }
  }, [capturarDatos]);


  const inputArchivoRef = useRef(null);

  const descargarPlantilla = useCallback(() => {
    const filaEjemplo = {
      NombreCompleto: "Juan Pérez",
      Documento: "1234567890",
      Celular: "3001234567",
      Direccion: "TORRE 1 APARTAMENTO 101",
      CodigoCliente: "800236506",
      CorreoElectronico: "juan.perez@correo.com",
      Password: "clave123",
      Permiso: "RESIDENTE",
      TipoResidente: "PROPIETARIO/NO RESIDENTE",
    };

    const hoja = XLSX.utils.json_to_sheet([filaEjemplo], { header: COLUMNAS_PLANTILLA });
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Usuarios");
    XLSX.writeFile(libro, "plantilla_usuarios.xlsx");
  }, []);

  const validarFilaExcel = (fila, indice) => {
    const nombreCompleto = String(fila.NombreCompleto || "").trim();
    const documento = String(fila.Documento || "").trim() || "0";
    const celular = String(fila.Celular || "").trim();
    const direccion = String(fila.Direccion || "").trim();
    const codigoCliente = String(fila.CodigoCliente || "").trim();
    const correoElectronico = String(fila.CorreoElectronico || "").trim().toLowerCase();
    const password = String(fila.Password || "").trim();
    const permiso = String(fila.Permiso || "RESIDENTE").trim().toUpperCase();
    const tipoResidente = String(fila.TipoResidente || "").trim().toUpperCase();

    const errores = [];
    if (!nombreCompleto) errores.push("nombre completo");
    if (!celular) errores.push("celular");
    if (!direccion) errores.push("dirección");
    if (!codigoCliente) errores.push("código de cliente");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!correoElectronico || !emailRegex.test(correoElectronico)) {
      errores.push("correo electrónico válido");
    } else if (/[#$[\]/]/.test(correoElectronico)) {
      errores.push("correo sin caracteres especiales (# $ [ ] /)");
    }
    if (!password || password.length < 6) errores.push("contraseña (mínimo 6 caracteres)");
    if (!ROLES_OPCIONES.includes(permiso)) errores.push(`permiso válido (${ROLES_OPCIONES.join(", ")})`);
    if (tipoResidente && !TIPOS_RESIDENTE_OPCIONES.includes(tipoResidente)) {
      errores.push(`tipo de residente válido (${TIPOS_RESIDENTE_OPCIONES.join(", ")})`);
    }

    if (errores.length > 0) {
      return { error: `Fila ${indice}: faltan o son inválidos: ${errores.join(", ")}` };
    }

    return {
      nombreCompleto,
      documento: documento.length < 4 ? "0" : documento,
      celular,
      direccion,
      codigoCliente,
      correoElectronico,
      password,
      permiso,
      tipoResidente,
    };
  };

  const procesarArchivoUsuarios = useCallback(async (archivo) => {
    try {
      const buffer = await archivo.arrayBuffer();
      const libro = XLSX.read(buffer, { type: "array" });
      const primeraHoja = libro.Sheets[libro.SheetNames[0]];
      const filas = XLSX.utils.sheet_to_json(primeraHoja, { defval: "" });

      const esFilaVacia = (fila) =>
        COLUMNAS_PLANTILLA.every((col) => String(fila[col] || "").trim() === "");

      const filasValidas = [];
      const erroresValidacion = [];
      filas.forEach((fila, i) => {
        if (esFilaVacia(fila)) return;
        const resultado = validarFilaExcel(fila, i + 2);
        if (resultado.error) erroresValidacion.push(resultado.error);
        else filasValidas.push(resultado);
      });

      if (!filasValidas.length && !erroresValidacion.length) {
        Swal.fire({ icon: "warning", title: "Archivo vacío", text: "El archivo no contiene registros con datos." });
        return;
      }

      if (erroresValidacion.length > 0) {
        Swal.fire({
          icon: "error",
          title: "Errores en el archivo",
          html: `<div style="text-align:left;max-height:300px;overflow-y:auto;font-size:13px;">${erroresValidacion
            .map((e) => `<p style="margin:4px 0;">${e}</p>`)
            .join("")}</div>`,
        });
        return;
      }

      const total = filasValidas.length;
      const resultados = [];

      Swal.fire({
        title: "Creando usuarios",
        html: `
          <div style="text-align:left;padding:5px 5px 0;">
            <p id="bulk-detalle" style="margin:0 0 10px;color:#7f8c8d;font-size:13px;">Procesando 0/${total}...</p>
            <div style="background:#ecf0f1;border-radius:10px;overflow:hidden;height:18px;">
              <div id="bulk-barra" style="width:0%;height:100%;background:#c0392b;transition:width .25s ease;"></div>
            </div>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      const autorizoUsuario = localStorage.getItem("CorreoElectronico") || "";

      for (let inicio = 0; inicio < total; inicio += TAMANO_LOTE_MASIVO) {
        const lote = filasValidas.slice(inicio, inicio + TAMANO_LOTE_MASIVO);
        const usuariosLote = lote.map((f) => ({
          NombreCompleto: f.nombreCompleto,
          Documento: f.documento,
          Celular: f.celular,
          Direccion: f.direccion,
          CodigoCliente: f.codigoCliente,
          CorreoElectronico: f.correoElectronico,
          Password: f.password,
          Permiso: f.permiso,
          TipoResidente: f.tipoResidente,
        }));

        try {
          const resp = await fetch(URL_REGISTRO_MASIVO, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ usuarios: usuariosLote, autorizoUsuario }),
          });
          const data = await resp.json().catch(() => null);

          if (resp.ok && Array.isArray(data?.body?.resultados)) {
            resultados.push(...data.body.resultados);
          } else {
            const motivo = typeof data?.body === "string" && data.body
              ? data.body
              : `El servidor respondió ${resp.status}`;
            lote.forEach((f) => resultados.push({ correo: f.correoElectronico, estado: "error", detalle: motivo }));
          }
        } catch (e) {
          lote.forEach((f) => resultados.push({ correo: f.correoElectronico, estado: "error", detalle: `Sin conexión con el servidor: ${e.message}` }));
        }

        const pct = Math.round((resultados.length / total) * 100);
        const barra = document.getElementById("bulk-barra");
        const detalle = document.getElementById("bulk-detalle");
        if (barra) barra.style.width = `${pct}%`;
        if (detalle) detalle.textContent = `Procesando ${resultados.length}/${total}...`;
      }

      capturarDatos();

      const creados = resultados.filter((r) => r.estado === "creado").length;
      const omitidos = resultados.filter((r) => r.estado === "omitido");
      const fallidos = resultados.filter((r) => r.estado === "error");

      const listaHtml = [...fallidos, ...omitidos]
        .map((r) => `<p style="margin:4px 0;">${r.correo}: ${r.detalle}</p>`)
        .join("");

      const { isDenied } = await Swal.fire({
        icon: fallidos.length ? "warning" : "success",
        title: fallidos.length ? "Carga finalizada con errores" : "Carga completada",
        html: `
          <p style="font-size:13px;color:#7f8c8d;margin:0 0 8px;">
            Creados: <b>${creados}</b> · Ya existían (omitidos): <b>${omitidos.length}</b> · Con error: <b>${fallidos.length}</b>
          </p>
          ${listaHtml ? `<div style="text-align:left;max-height:250px;overflow-y:auto;font-size:12px;border-top:1px solid #eee;padding-top:6px;">${listaHtml}</div>` : ""}
        `,
        showDenyButton: true,
        denyButtonText: "Descargar reporte",
        denyButtonColor: "#7f8c8d",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#c0392b",
      });

      if (isDenied) {
        const hojaReporte = XLSX.utils.json_to_sheet(
          resultados.map((r) => ({
            CorreoElectronico: r.correo,
            Resultado: r.estado,
            Detalle: r.detalle || "",
          }))
        );
        const libroReporte = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(libroReporte, hojaReporte, "Resultado");
        XLSX.writeFile(libroReporte, "reporte_carga_usuarios.xlsx");
      }
    } catch (error) {
      console.error("Error procesando archivo:", error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo procesar el archivo: " + error.message });
    }
  }, [capturarDatos]);

  const abrirSelectorArchivo = useCallback(() => {
    inputArchivoRef.current?.click();
  }, []);

  const manejarArchivoSeleccionado = useCallback(async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo) return;

    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "Cargar usuarios",
      text: `¿Deseas registrar los usuarios contenidos en "${archivo.name}"? Los correos que ya estén registrados se omitirán y solo se crearán los faltantes.`,
      showCancelButton: true,
      confirmButtonText: "Sí, cargar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    await procesarArchivoUsuarios(archivo);
  }, [procesarArchivoUsuarios]);

  const editarUsuario = useCallback(async (fila) => {
    const { value: formValues } = await Swal.fire({
      title: "Editar Usuario",
      width: 600,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d6efd",
      html: `
        <style>
          .swal2-input, .swal2-select { margin: 0 !important; width: 100% !important; box-sizing: border-box !important; padding: 10px !important; border: 1px solid #d1d5db !important; border-radius: 5px !important; font-size: 14px !important; }
          .eu-row { display: flex; gap: 10px; margin-bottom: 12px; }
          .eu-row > div { flex: 1; }
          .eu-field { margin-bottom: 12px; }
          .eu-label { display: block; margin-bottom: 5px; color: #555; font-size: 13px; font-weight: 500; text-align: left; }
        </style>
        <div style="text-align:left; padding: 10px 20px 0;">
          <div class="eu-field">
            <label class="eu-label">Nombre completo</label>
            <input id="eu-nombre" class="swal2-input" value="${fila.NombreCompleto || ""}">
          </div>
          <div class="eu-field">
            <label class="eu-label">Celular</label>
            <input id="eu-celular" class="swal2-input" value="${fila.Celular || ""}">
          </div>
          <div class="eu-field">
            <label class="eu-label">Dirección</label>
            <input id="eu-direccion" class="swal2-input" value="${fila.Direccion || ""}">
          </div>
          <div class="eu-field">
            <label class="eu-label">NIT de la copropiedad</label>
            <input id="eu-nit" class="swal2-input" value="${fila.CodigoCliente || ""}">
          </div>
          <div class="eu-row">
            <div>
              <label class="eu-label">Estado</label>
              <select id="eu-estado" class="swal2-select">
                <option value="PENDIENTE" ${fila.estadoregistro === "PENDIENTE" ? "selected" : ""}>PENDIENTE</option>
                <option value="AUTORIZADO" ${fila.estadoregistro === "AUTORIZADO" ? "selected" : ""}>AUTORIZADO</option>
                <option value="RECHAZADO" ${fila.estadoregistro === "RECHAZADO" ? "selected" : ""}>RECHAZADO</option>
                <option value="ELIMINADO" ${fila.estadoregistro === "ELIMINADO" ? "selected" : ""}>ELIMINADO</option>
                <option value="SIN CONFIRMAR CORREO" ${fila.estadoregistro === "SIN CONFIRMAR CORREO" ? "selected" : ""}>SIN CONFIRMAR CORREO</option>
              </select>
            </div>
            <div>
              <label class="eu-label">Permiso</label>
              <select id="eu-permiso" class="swal2-select">
                <option value="RESIDENTE" ${fila.Permisos === "RESIDENTE" ? "selected" : ""}>RESIDENTE</option>
                <option value="ADMINISTRADOR" ${fila.Permisos === "ADMINISTRADOR" ? "selected" : ""}>ADMINISTRADOR</option>
                <option value="GUARDA" ${fila.Permisos === "GUARDA" ? "selected" : ""}>GUARDA</option>
                <option value="AUDITOR" ${fila.Permisos === "AUDITOR" ? "selected" : ""}>AUDITOR</option>
                <option value="CONTADOR" ${fila.Permisos === "CONTADOR" ? "selected" : ""}>CONTADOR</option>
                ${puedeAsignarSuper() || fila.Permisos === "SUPER" ? `<option value="SUPER" ${fila.Permisos === "SUPER" ? "selected" : ""}>SUPER</option>` : ""}
              </select>
            </div>
          </div>
          <div class="eu-field">
            <label class="eu-label">Tipo de Residente</label>
            <select id="eu-tipo-residente" class="swal2-select">
              <option value="">-- Seleccionar --</option>
              ${TIPOS_RESIDENTE_OPCIONES.map((t) => `<option value="${t}" ${fila.tipo_residente === t ? "selected" : ""}>${t}</option>`).join("")}
            </select>
          </div>
        </div>
      `,
      preConfirm: () => {
        const nombre = document.getElementById("eu-nombre").value.trim();
        const celular = document.getElementById("eu-celular").value.trim();
        const direccion = document.getElementById("eu-direccion").value.trim();
        const nit = document.getElementById("eu-nit").value.trim();
        const estado = document.getElementById("eu-estado").value;
        const permiso = document.getElementById("eu-permiso").value;
        const tipoResidente = document.getElementById("eu-tipo-residente").value;

        if (!nombre || !celular || !direccion || !nit) {
          Swal.showValidationMessage("Nombre, celular, dirección y NIT son obligatorios");
          return false;
        }
        return { nombre, celular, direccion, nit, estado, permiso, tipoResidente };
      },
    });

    if (!formValues) return;

    try {
      const correo = fila.CorreoElectronico.replaceAll("@", "").replaceAll(".", "");
      const nitAnterior = fila.CodigoCliente;
      const estadoAnterior = fila.estadoregistro;
      const { nombre, celular, direccion, nit, estado, permiso, tipoResidente } = formValues;

      await actualizarRegistro(`usuarios/${correo}/${fila.id}/NombreCompleto`, nombre);
      await actualizarRegistro(`usuarios/${correo}/${fila.id}/Celular`, celular);
      await actualizarRegistro(`usuarios/${correo}/${fila.id}/Direccion`, direccion);
      await actualizarRegistro(`usuarios/${correo}/${fila.id}/CodigoCliente`, nit);
      await actualizarRegistro(`usuarios/${correo}/${fila.id}/Permisos`, permiso);
      await actualizarRegistro(`usuarios/${correo}/${fila.id}/tipo_residente`, tipoResidente);

      if (estado !== estadoAnterior) {
        if (nit !== nitAnterior) {
          await limpiarRegistrosCorreo("pendientes", nitAnterior, correo);
          await limpiarRegistrosCorreo("autorizados", nitAnterior, correo);
        }
        await aprobarUsuarioWeb({ ...fila, CodigoCliente: nit }, estado);
      } else if (nit !== nitAnterior) {
        await limpiarRegistrosCorreo("pendientes", nitAnterior, correo);
        await limpiarRegistrosCorreo("autorizados", nitAnterior, correo);

        if (estado === "AUTORIZADO") {
          await crearRegistro(`autorizados/${nit}`, {
            ruta: `usuarios/${correo}/${fila.id}`,
          });
        } else {
          await actualizarRegistro(`pendientes/${nit}/${correo}`, {
            ruta: `usuarios/${correo}/${fila.id}`,
          });
        }
      }

      Swal.fire({ icon: "success", title: "Usuario actualizado", confirmButtonText: "Aceptar" });
      capturarDatos();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.message, confirmButtonText: "Aceptar" });
    }
  }, [capturarDatos]);


  const rutaInmuebles = (fila) => {
    const correo = (fila.CorreoElectronico || "").replaceAll("@", "").replaceAll(".", "");
    return `usuarios/${correo}/inmueble`;
  };

  const selectorPermisos = (valorActual = "") => `
    <select id="inm-permiso" class="swal2-input" style="cursor:pointer;">
      <option value="">-- Seleccionar --</option>
      ${ROLES_OPCIONES.map((r) => `<option value="${r}" ${r === valorActual ? "selected" : ""}>${r}</option>`).join("")}
    </select>
  `;

  const formularioInmueble = (inmueble = {}) => `
    <style>
      #inm-form .swal2-input, #inm-form select.swal2-input {
        margin: 0 !important; width: 100% !important; box-sizing: border-box !important;
        padding: 10px !important; border: 1px solid #d1d5db !important;
        border-radius: 5px !important; font-size: 14px !important;
      }
      #inm-form label { display:block; margin-bottom:6px; color:#555; font-size:14px; font-weight:500; }
      #inm-form .inm-field { margin-bottom:15px; }
    </style>
    <div id="inm-form" style="text-align:left;padding:10px 20px 0;">
      <div class="inm-field">
        <label>Cliente / Copropiedad</label>
        <input id="inm-cliente" class="swal2-input" placeholder="Nombre del cliente o propietario" value="${inmueble.cliente || ""}">
      </div>
      <div class="inm-field">
        <label>Código del Inmueble</label>
        <input id="inm-codigo" class="swal2-input" placeholder="Ej: 101, APTO-202" value="${inmueble.codigo || ""}">
      </div>
      <div class="inm-field">
        <label>Dirección</label>
        <input id="inm-direccion" class="swal2-input" placeholder="Dirección del inmueble" value="${inmueble.direccion || ""}">
      </div>
      <div class="inm-field">
        <label>Permiso</label>
        ${selectorPermisos(inmueble.permiso)}
      </div>
      <div class="inm-field">
        <label>Estado</label>
        <select id="inm-estado" class="swal2-input" style="cursor:pointer;">
          <option value="AUTORIZADO" ${(inmueble.estado || "AUTORIZADO") === "AUTORIZADO" ? "selected" : ""}>AUTORIZADO</option>
          <option value="RECHAZADO" ${inmueble.estado === "RECHAZADO" ? "selected" : ""}>RECHAZADO</option>
        </select>
      </div>
    </div>
  `;

  const leerFormularioInmueble = () => {
    const codigo = document.getElementById("inm-codigo").value.trim();
    const direccion = document.getElementById("inm-direccion").value.trim();
    const cliente = document.getElementById("inm-cliente").value.trim();
    const permiso = document.getElementById("inm-permiso").value;
    const estado = document.getElementById("inm-estado").value;

    if (!codigo) { Swal.showValidationMessage("El código es obligatorio"); return false; }
    if (!direccion) { Swal.showValidationMessage("La dirección es obligatoria"); return false; }
    if (!cliente) { Swal.showValidationMessage("El cliente es obligatorio"); return false; }

    return { codigo, direccion, cliente, permiso, estado };
  };

  const editarInmuebleUsuario = async (fila, inmueble = null, _id = null) => {
    const esNuevo = !_id;
    const { value: formValues } = await Swal.fire({
      title: esNuevo ? "Agregar Inmueble" : "Editar Inmueble",
      html: formularioInmueble(inmueble || {}),
      showCancelButton: true,
      confirmButtonText: esNuevo ? "Agregar" : "Guardar",
      cancelButtonText: "Cancelar",
      width: 550,
      preConfirm: leerFormularioInmueble,
    });

    if (!formValues) return false;

    try {
      if (esNuevo) {
        await crearRegistro(rutaInmuebles(fila), formValues);
      } else {
        await actualizarRegistro(`${rutaInmuebles(fila)}/${_id}`, formValues);
      }
      return true;
    } catch {
      Swal.fire("Error", `No se pudo ${esNuevo ? "agregar" : "actualizar"} el inmueble.`, "error");
      return false;
    }
  };

  const confirmarEliminarInmueble = async (fila, inmueble, _id) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Quitar Inmueble?",
      html: `
        <div style="text-align:left;padding:15px;">
          <div style="background:#f8f9fa;padding:15px;border-radius:8px;border-left:4px solid #e74c3c;">
            <strong style="display:block;margin-bottom:8px;color:#2c3e50;">${inmueble.codigo} — ${inmueble.direccion}</strong>
            <p style="color:#7f8c8d;margin:5px 0;font-size:14px;">Cliente: ${inmueble.cliente || "-"}</p>
            <p style="color:#7f8c8d;margin:0;font-size:14px;">Permiso: ${inmueble.permiso || "-"}</p>
          </div>
          <p style="margin-top:15px;color:#e74c3c;">Esta acción no se puede deshacer.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Quitar",
      cancelButtonText: "Cancelar",
    });

    if (!isConfirmed) return false;

    try {
      await eliminarRegistro(`${rutaInmuebles(fila)}/${_id}`);
      return true;
    } catch {
      Swal.fire("Error", "No se pudo quitar el inmueble.", "error");
      return false;
    }
  };

  const gestionarInmuebles = useCallback(async (fila) => {
    const cargarYRenderizar = async () => {
      const data = await consultarSimple(rutaInmuebles(fila));
      const inmuebles = Object.entries(data || {})
        .filter(([, val]) => val && typeof val === "object")
        .map(([_id, val]) => ({ _id, ...val }));

      const filas = inmuebles.length
        ? inmuebles
            .map(
              (inm) => `
          <tr>
            <td style="padding:8px 10px;border-bottom:1px solid #eee;">${inm.codigo || "-"}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #eee;">${inm.direccion || "-"}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #eee;">${inm.cliente || "-"}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #eee;">${inm.permiso || "-"}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #eee;">
              <span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:500;color:#fff;white-space:nowrap;background:#95a5a6;">${inm.estado || "AUTORIZADO"}</span>
            </td>
            <td style="padding:8px 10px;border-bottom:1px solid #eee;white-space:nowrap;">
              <button class="inm-edit" data-id="${inm._id}" style="border:none;background:#c0392b;color:#fff;padding:4px 10px;border-radius:5px;cursor:pointer;font-size:12px;margin-right:4px;">Editar</button>
              <button class="inm-del" data-id="${inm._id}" style="border:none;background:#c0392b;color:#fff;padding:4px 10px;border-radius:5px;cursor:pointer;font-size:12px;">Quitar</button>
            </td>
          </tr>`
            )
            .join("")
        : `<tr><td colspan="6" style="padding:20px;text-align:center;color:#95a5a6;">Este usuario no tiene inmuebles.</td></tr>`;

      return { inmuebles, html: `
        <div style="text-align:left;">
          <p style="margin:0 0 15px;color:#7f8c8d;font-size:13px;">
            Usuario: <strong>${fila.NombreCompleto || fila.CorreoElectronico}</strong>
          </p>
          <div style="overflow-x:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              <thead>
                <tr style="background:#f8f9fa;">
                  <th style="padding:8px 10px;text-align:left;color:#2c3e50;">Código</th>
                  <th style="padding:8px 10px;text-align:left;color:#2c3e50;">Dirección</th>
                  <th style="padding:8px 10px;text-align:left;color:#2c3e50;">Cliente</th>
                  <th style="padding:8px 10px;text-align:left;color:#2c3e50;">Permiso</th>
                  <th style="padding:8px 10px;text-align:left;color:#2c3e50;">Estado</th>
                  <th style="padding:8px 10px;text-align:left;color:#2c3e50;">Acciones</th>
                </tr>
              </thead>
              <tbody>${filas}</tbody>
            </table>
          </div>
        </div>
      ` };
    };

    const abrir = async () => {
      const { inmuebles, html } = await cargarYRenderizar();

      await Swal.fire({
        title: "Inmuebles del Usuario",
        html,
        width: 800,
        showConfirmButton: true,
        confirmButtonText: "Agregar Inmueble",
        showCancelButton: true,
        cancelButtonText: "Cerrar",
        confirmButtonColor: "#c0392b",
        didOpen: () => {
          const popup = Swal.getPopup();
          popup.querySelectorAll(".inm-edit").forEach((btn) => {
            btn.addEventListener("click", async () => {
              const inm = inmuebles.find((i) => i._id === btn.dataset.id);
              Swal.close();
              await editarInmuebleUsuario(fila, inm, btn.dataset.id);
              await abrir();
            });
          });
          popup.querySelectorAll(".inm-del").forEach((btn) => {
            btn.addEventListener("click", async () => {
              const inm = inmuebles.find((i) => i._id === btn.dataset.id);
              Swal.close();
              await confirmarEliminarInmueble(fila, inm, btn.dataset.id);
              await abrir();
            });
          });
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          const ok = await editarInmuebleUsuario(fila, null, null);
          if (ok) {
            await Swal.fire({ icon: "success", title: "Inmueble agregado", timer: 1500, showConfirmButton: false });
          }
          await abrir();
        }
      });
    };

    await abrir();
  }, []);

  const excluirUsuario = useCallback(async (fila) => {
    const correoOriginal = (fila.CorreoElectronico || "").toLowerCase().trim();
    if (!correoOriginal) {
      Swal.fire({ icon: "error", title: "Error", text: "El usuario no tiene correo electrónico." });
      return;
    }

    const { isConfirmed } = await Swal.fire({
      title: "¿Excluir de notificaciones?",
      html: `
        <div style="text-align:left;padding:15px;">
          <div style="background:#f8f9fa;padding:15px;border-radius:8px;border-left:4px solid #c0392b;">
            <strong style="display:block;margin-bottom:8px;color:#2c3e50;">${fila.NombreCompleto || correoOriginal}</strong>
            <p style="color:#7f8c8d;margin:0;font-size:14px;">${correoOriginal}</p>
          </div>
          <p style="margin-top:15px;color:#7f8c8d;font-size:13px;">${MOTIVO_EXCLUSION_DEFECTO}</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c0392b",
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
    });

    if (!isConfirmed) return;

    try {
      const clave = correoOriginal.replaceAll("@", "").replaceAll(".", "");
      await actualizarRegistro(`notificaciones_excluidas/${clave}`, MOTIVO_EXCLUSION_DEFECTO);
      Swal.fire({ icon: "success", title: "Usuario excluido", text: "El correo fue agregado a la lista de excluidos.", timer: 2000, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo excluir el usuario." });
    }
  }, []);

  const abrirOpciones = useCallback(async (fila) => {
    const admin = esAdministrador();

    const opciones = [
      { id: "editar", texto: "Editar", icono: "✏️" },
      { id: "duplicar", texto: "Duplicar", icono: "📋" },
      ...(admin
        ? [
            { id: "inmuebles", texto: "Inmuebles", icono: "🏢" },
            { id: "excluir", texto: "Excluir de notificaciones", icono: "🔕" },
          ]
        : []),
    ];

    let eleccion = null;
    await Swal.fire({
      title: "Opciones",
      html: `
        <p style="margin:0 0 15px;color:#7f8c8d;font-size:13px;">
          ${fila.NombreCompleto || fila.CorreoElectronico}
        </p>
        <div id="opciones-menu" style="display:flex;flex-direction:column;gap:8px;">
          ${opciones
            .map(
              (op) => `
            <button class="opcion-btn" data-id="${op.id}"
              style="width:100%;text-align:left;padding:12px 16px;border:1px solid #e0e0e0;border-radius:8px;background:#fff;cursor:pointer;font-size:14px;color:#2c3e50;transition:background .15s;"
              onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='#fff'">
              <span style="margin-right:8px;">${op.icono}</span>${op.texto}
            </button>`
            )
            .join("")}
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Cerrar",
      width: 420,
      didOpen: () => {
        Swal.getPopup().querySelectorAll(".opcion-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            eleccion = btn.dataset.id;
            Swal.close();
          });
        });
      },
    });

    if (eleccion === "editar") await editarUsuario(fila);
    else if (eleccion === "duplicar") await crearUsuario(fila);
    else if (eleccion === "inmuebles") await gestionarInmuebles(fila);
    else if (eleccion === "excluir") await excluirUsuario(fila);
  }, [editarUsuario, crearUsuario, gestionarInmuebles, excluirUsuario]);

  const normalizarGuardasMasivo = useCallback(async () => {
    const TELEFONO_DESTINO = "6063447575";

    const cambios = datos.filter((u) => u.Permisos === "GUARDA").map((usuario) => {
      const cambio = { usuario, nombreNuevo: null, direccionNueva: null, telefonoNuevo: null };

      const nombreActual = usuario.NombreCompleto || "";
      const nombreMayus = nombreActual.toUpperCase();
      if (nombreMayus !== nombreActual) cambio.nombreNuevo = nombreMayus;

      const direccionActual = usuario.Direccion || "";
      const direccionMayus = direccionActual.toUpperCase();
      if (direccionMayus !== direccionActual) cambio.direccionNueva = direccionMayus;

      const telefonoActual = String(usuario.Celular ?? "").trim();
      if (telefonoActual !== TELEFONO_DESTINO) cambio.telefonoNuevo = TELEFONO_DESTINO;

      return cambio;
    }).filter((c) => c.nombreNuevo !== null || c.direccionNueva !== null || c.telefonoNuevo !== null);

    if (cambios.length === 0) {
      Swal.fire("Sin cambios", "No hay guardas que requieran actualización.", "info");
      return;
    }

    const filasHtml = cambios.map((c) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${c.usuario.CorreoElectronico || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          ${c.nombreNuevo !== null
            ? `<span style="color:#c0392b;">${c.usuario.NombreCompleto || "-"}</span> → <strong>${c.nombreNuevo}</strong>`
            : "-"}
        </td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          ${c.direccionNueva !== null
            ? `<span style="color:#c0392b;">${c.usuario.Direccion || "-"}</span> → <strong>${c.direccionNueva}</strong>`
            : "-"}
        </td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          ${c.telefonoNuevo !== null
            ? `<span style="color:#c0392b;">${c.usuario.Celular ?? "-"}</span> → <strong>${c.telefonoNuevo}</strong>`
            : "-"}
        </td>
      </tr>
    `).join("");

    const { isConfirmed } = await Swal.fire({
      title: `Se van a actualizar ${cambios.length} guarda(s)`,
      html: `
        <div style="text-align:left;max-height:400px;overflow-y:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead>
              <tr style="background:#f8f9fa;">
                <th style="padding:6px 8px;text-align:left;">Correo</th>
                <th style="padding:6px 8px;text-align:left;">Nombre</th>
                <th style="padding:6px 8px;text-align:left;">Dirección</th>
                <th style="padding:6px 8px;text-align:left;">Teléfono</th>
              </tr>
            </thead>
            <tbody>${filasHtml}</tbody>
          </table>
        </div>
      `,
      width: 900,
      showCancelButton: true,
      confirmButtonText: "Aplicar cambios",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      for (const c of cambios) {
        const correo = (c.usuario.CorreoElectronico || "").replaceAll("@", "").replaceAll(".", "");
        const ruta = `usuarios/${correo}/${c.usuario.id}`;

        if (c.nombreNuevo !== null) {
          await actualizarRegistro(`${ruta}/NombreCompleto`, c.nombreNuevo);
        }
        if (c.direccionNueva !== null) {
          await actualizarRegistro(`${ruta}/Direccion`, c.direccionNueva);
        }
        if (c.telefonoNuevo !== null) {
          await actualizarRegistro(`${ruta}/Celular`, c.telefonoNuevo);
        }
      }

      Swal.fire("Éxito", `Se actualizaron ${cambios.length} guarda(s) correctamente`, "success");
      capturarDatos();
    } catch (error) {
      console.error("Error en normalización masiva de guardas:", error);
      Swal.fire("Error", "Ocurrió un error durante la actualización masiva", "error");
    }
  }, [datos, capturarDatos]);

  const rechazarPorTelefonoMasivo = useCallback(async () => {
    const TELEFONO_ORIGEN = "3017962402";
    const TELEFONO_DESTINO = "6063447575";

    const cambios = datos
      .filter((u) => String(u.Celular ?? "").trim() === TELEFONO_ORIGEN)
      .map((usuario) => ({ usuario, telefonoNuevo: TELEFONO_DESTINO, estadoNuevo: "RECHAZADO" }));

    if (cambios.length === 0) {
      Swal.fire("Sin cambios", `No hay usuarios con el celular ${TELEFONO_ORIGEN}.`, "info");
      return;
    }

    const filasHtml = cambios.map((c) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${c.usuario.CorreoElectronico || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${c.usuario.NombreCompleto || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          <span style="color:#c0392b;">${c.usuario.Celular ?? "-"}</span> → <strong>${c.telefonoNuevo}</strong>
        </td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          <span style="color:#c0392b;">${c.usuario.estadoregistro || "-"}</span> → <strong>${c.estadoNuevo}</strong>
        </td>
      </tr>
    `).join("");

    const { isConfirmed } = await Swal.fire({
      title: `Se van a actualizar ${cambios.length} usuario(s)`,
      html: `
        <div style="text-align:left;max-height:400px;overflow-y:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead>
              <tr style="background:#f8f9fa;">
                <th style="padding:6px 8px;text-align:left;">Correo</th>
                <th style="padding:6px 8px;text-align:left;">Nombre</th>
                <th style="padding:6px 8px;text-align:left;">Teléfono</th>
                <th style="padding:6px 8px;text-align:left;">Estado</th>
              </tr>
            </thead>
            <tbody>${filasHtml}</tbody>
          </table>
        </div>
      `,
      width: 900,
      showCancelButton: true,
      confirmButtonText: "Aplicar cambios",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      for (const c of cambios) {
        const correo = (c.usuario.CorreoElectronico || "").replaceAll("@", "").replaceAll(".", "");
        const ruta = `usuarios/${correo}/${c.usuario.id}`;

        await actualizarRegistro(`${ruta}/Celular`, c.telefonoNuevo);
        await actualizarRegistro(`${ruta}/estadoregistro`, c.estadoNuevo);
      }

      Swal.fire("Éxito", `Se actualizaron ${cambios.length} usuario(s) correctamente`, "success");
      capturarDatos();
    } catch (error) {
      console.error("Error en actualización masiva por teléfono:", error);
      Swal.fire("Error", "Ocurrió un error durante la actualización masiva", "error");
    }
  }, [datos, capturarDatos]);

  const MOTIVO_AUTOELIMINACION = "El usuario eliminó su cuenta de la plataforma por cuenta propia.";

  const eliminarRechazadosMasivo = useCallback(async () => {
    const objetivo = datos.filter((u) => u.estadoregistro === "RECHAZADO" || u.estadoregistro === "ELIMINADO");

    if (objetivo.length === 0) {
      Swal.fire("Sin registros", "No hay usuarios RECHAZADOS ni ELIMINADOS.", "info");
      return;
    }

    const filasHtml = objetivo.map((u) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.CorreoElectronico || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.NombreCompleto || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.CodigoCliente || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.estadoregistro || "-"}</td>
      </tr>
    `).join("");

    const { isConfirmed } = await Swal.fire({
      title: `Se van a ELIMINAR ${objetivo.length} usuario(s)`,
      html: `
        <div style="text-align:left;">
          <p style="color:#c0392b;font-size:13px;margin:0 0 10px;">
            Esta acción borra el usuario y sus referencias en correos/, autorizados/ y pendientes/. Los ELIMINADO además quedan
            registrados en notificaciones_excluidas. No se puede deshacer.
          </p>
          <div style="max-height:350px;overflow-y:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:12px;">
              <thead>
                <tr style="background:#f8f9fa;">
                  <th style="padding:6px 8px;text-align:left;">Correo</th>
                  <th style="padding:6px 8px;text-align:left;">Nombre</th>
                  <th style="padding:6px 8px;text-align:left;">Código Cliente</th>
                  <th style="padding:6px 8px;text-align:left;">Estado</th>
                </tr>
              </thead>
              <tbody>${filasHtml}</tbody>
            </table>
          </div>
        </div>
      `,
      width: 900,
      showCancelButton: true,
      confirmButtonText: "Eliminar definitivamente",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      for (const u of objetivo) {
        const correo = (u.CorreoElectronico || "").toLowerCase().replaceAll("@", "").replaceAll(".", "");
        const codigo = u.CodigoCliente;

        if (u.estadoregistro === "ELIMINADO" && correo) {
          await actualizarRegistro(`notificaciones_excluidas/${correo}`, MOTIVO_AUTOELIMINACION);
        }

        await limpiarRegistrosCorreo("pendientes", codigo, correo);
        await limpiarRegistrosCorreo("autorizados", codigo, correo);

        const idEnCorreos = await consultarSimple(`correos/${correo}`);
        if (idEnCorreos === u.id) {
          await eliminarRegistro(`correos/${correo}`);
        }

        await eliminarRegistro(`usuarios/${correo}/${u.id}`);
      }

      Swal.fire("Éxito", `Se eliminaron ${objetivo.length} usuario(s) correctamente`, "success");
      capturarDatos();
    } catch (error) {
      console.error("Error eliminando usuarios rechazados/eliminados:", error);
      Swal.fire("Error", "Ocurrió un error durante la eliminación masiva", "error");
    }
  }, [datos, capturarDatos]);

  const autorizarPendientesMasivo = useCallback(async () => {
    const objetivo = datos.filter(
      (u) => u.estadoregistro === "PENDIENTE" || u.estadoregistro === "SIN CONFIRMAR CORREO"
    );

    if (objetivo.length === 0) {
      Swal.fire("Sin registros", "No hay usuarios PENDIENTE ni SIN CONFIRMAR CORREO.", "info");
      return;
    }

    const filasHtml = objetivo.map((u) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.CorreoElectronico || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.NombreCompleto || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.CodigoCliente || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.estadoregistro || "-"}</td>
      </tr>
    `).join("");

    const { isConfirmed } = await Swal.fire({
      title: `Se van a AUTORIZAR ${objetivo.length} usuario(s)`,
      html: `
        <div style="text-align:left;">
          <p style="color:#7f8c8d;font-size:13px;margin:0 0 10px;">
            Cada usuario quedará AUTORIZADO y se creará su registro en autorizados/{CodigoCliente}, igual que al aprobar uno individualmente.
          </p>
          <div style="max-height:350px;overflow-y:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:12px;">
              <thead>
                <tr style="background:#f8f9fa;">
                  <th style="padding:6px 8px;text-align:left;">Correo</th>
                  <th style="padding:6px 8px;text-align:left;">Nombre</th>
                  <th style="padding:6px 8px;text-align:left;">Código Cliente</th>
                  <th style="padding:6px 8px;text-align:left;">Estado actual</th>
                </tr>
              </thead>
              <tbody>${filasHtml}</tbody>
            </table>
          </div>
        </div>
      `,
      width: 900,
      showCancelButton: true,
      confirmButtonText: "Autorizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      const fallidos = [];
      for (const u of objetivo) {
        const ok = await aprobarUsuarioWeb(u, "AUTORIZADO");
        if (!ok) fallidos.push(u.CorreoElectronico || u.id);
      }

      capturarDatos();

      if (fallidos.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Autorización finalizada con errores",
          html: `Se autorizaron ${objetivo.length - fallidos.length} de ${objetivo.length}.<br>Fallaron: ${fallidos.join(", ")}`,
        });
      } else {
        Swal.fire("Éxito", `Se autorizaron ${objetivo.length} usuario(s) correctamente`, "success");
      }
    } catch (error) {
      console.error("Error autorizando usuarios pendientes:", error);
      Swal.fire("Error", "Ocurrió un error durante la autorización masiva", "error");
    }
  }, [datos, capturarDatos]);

  const limpiarUsuariosFantasmaMasivo = useCallback(async () => {
    const fantasmas = datos.filter((u) => !u.CorreoElectronico && !u.NombreCompleto && u.correo && u.id);

    if (fantasmas.length === 0) {
      Swal.fire("Sin registros", "No se encontraron usuarios fantasma.", "info");
      return;
    }

    const filasHtml = fantasmas.map((u) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.correo || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${u.id || "-"}</td>
      </tr>
    `).join("");

    const { isConfirmed } = await Swal.fire({
      title: `Se van a ELIMINAR ${fantasmas.length} puntero(s) huérfano(s)`,
      html: `
        <div style="text-align:left;">
          <p style="color:#c0392b;font-size:13px;margin:0 0 10px;">
            Estos son correos/{correo} cuyo usuarios/{correo}/{id} ya no existe o está vacío. Se borrará solo el puntero en correos/. No se puede deshacer.
          </p>
          <div style="max-height:350px;overflow-y:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:12px;">
              <thead>
                <tr style="background:#f8f9fa;">
                  <th style="padding:6px 8px;text-align:left;">Correo (correos/)</th>
                  <th style="padding:6px 8px;text-align:left;">Id apuntado</th>
                </tr>
              </thead>
              <tbody>${filasHtml}</tbody>
            </table>
          </div>
        </div>
      `,
      width: 700,
      showCancelButton: true,
      confirmButtonText: "Eliminar punteros",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      for (const u of fantasmas) {
        await eliminarRegistro(`correos/${u.correo}`);
      }

      Swal.fire("Éxito", `Se eliminaron ${fantasmas.length} puntero(s) huérfano(s)`, "success");
      capturarDatos();
    } catch (error) {
      console.error("Error eliminando usuarios fantasma:", error);
      Swal.fire("Error", "Ocurrió un error durante la limpieza", "error");
    }
  }, [datos, capturarDatos]);

  const normalizarMayusculasTodosMasivo = useCallback(async () => {
    const cambios = datos.map((usuario) => {
      const cambio = { usuario, nombreNuevo: null, direccionNueva: null };

      const nombreActual = usuario.NombreCompleto || "";
      const nombreMayus = nombreActual.toUpperCase();
      if (nombreMayus !== nombreActual) cambio.nombreNuevo = nombreMayus;

      const direccionActual = usuario.Direccion || "";
      const direccionMayus = direccionActual.toUpperCase();
      if (direccionMayus !== direccionActual) cambio.direccionNueva = direccionMayus;

      return cambio;
    }).filter((c) => c.nombreNuevo !== null || c.direccionNueva !== null);

    if (cambios.length === 0) {
      Swal.fire("Sin cambios", "No hay usuarios que requieran actualización.", "info");
      return;
    }

    const filasHtml = cambios.map((c) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${c.usuario.CorreoElectronico || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          ${c.nombreNuevo !== null
            ? `<span style="color:#c0392b;">${c.usuario.NombreCompleto || "-"}</span> → <strong>${c.nombreNuevo}</strong>`
            : "-"}
        </td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">
          ${c.direccionNueva !== null
            ? `<span style="color:#c0392b;">${c.usuario.Direccion || "-"}</span> → <strong>${c.direccionNueva}</strong>`
            : "-"}
        </td>
      </tr>
    `).join("");

    const { isConfirmed } = await Swal.fire({
      title: `Se van a actualizar ${cambios.length} usuario(s)`,
      html: `
        <div style="text-align:left;max-height:400px;overflow-y:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead>
              <tr style="background:#f8f9fa;">
                <th style="padding:6px 8px;text-align:left;">Correo</th>
                <th style="padding:6px 8px;text-align:left;">Nombre</th>
                <th style="padding:6px 8px;text-align:left;">Dirección</th>
              </tr>
            </thead>
            <tbody>${filasHtml}</tbody>
          </table>
        </div>
      `,
      width: 900,
      showCancelButton: true,
      confirmButtonText: "Aplicar cambios",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      const actualizaciones = {};
      for (const c of cambios) {
        const correo = (c.usuario.CorreoElectronico || "").replaceAll("@", "").replaceAll(".", "");
        const ruta = `usuarios/${correo}/${c.usuario.id}`;

        if (c.nombreNuevo !== null) actualizaciones[`${ruta}/NombreCompleto`] = c.nombreNuevo;
        if (c.direccionNueva !== null) actualizaciones[`${ruta}/Direccion`] = c.direccionNueva;
      }

      await actualizarMultiple(actualizaciones);

      Swal.fire("Éxito", `Se actualizaron ${cambios.length} usuario(s) correctamente`, "success");
      capturarDatos();
    } catch (error) {
      console.error("Error en normalización de mayúsculas:", error);
      Swal.fire("Error", "Ocurrió un error durante la actualización masiva", "error");
    }
  }, [datos, capturarDatos]);

  const abrirMenuAccionesTemporales = useCallback(async () => {
    const opciones = [
      { id: "guardas", texto: "Normalizar Guardas", icono: "🛡️", accion: normalizarGuardasMasivo },
      { id: "telefono", texto: "Rechazar por teléfono 3017962402", icono: "📵", accion: rechazarPorTelefonoMasivo },
      { id: "eliminar", texto: "Eliminar RECHAZADOS y ELIMINADOS", icono: "🗑️", accion: eliminarRechazadosMasivo },
      { id: "autorizar", texto: "Autorizar PENDIENTE y SIN CONFIRMAR CORREO", icono: "✅", accion: autorizarPendientesMasivo },
      { id: "fantasma", texto: "Limpiar usuarios fantasma", icono: "🧹", accion: limpiarUsuariosFantasmaMasivo },
      { id: "mayusculas", texto: "Nombre y Dirección en mayúsculas (todos)", icono: "🔠", accion: normalizarMayusculasTodosMasivo },
    ];

    let eleccion = null;
    await Swal.fire({
      title: "Acciones masivas (temporal)",
      html: `
        <div id="acciones-temp-menu" style="display:flex;flex-direction:column;gap:8px;">
          ${opciones
            .map(
              (op) => `
            <button class="accion-temp-btn" data-id="${op.id}"
              style="width:100%;text-align:left;padding:12px 16px;border:1px solid #e0e0e0;border-radius:8px;background:#fff;cursor:pointer;font-size:14px;color:#2c3e50;transition:background .15s;"
              onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='#fff'">
              <span style="margin-right:8px;">${op.icono}</span>${op.texto}
            </button>`
            )
            .join("")}
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Cerrar",
      width: 480,
      didOpen: () => {
        Swal.getPopup().querySelectorAll(".accion-temp-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            eleccion = btn.dataset.id;
            Swal.close();
          });
        });
      },
    });

    const opcionElegida = opciones.find((op) => op.id === eleccion);
    if (opcionElegida) await opcionElegida.accion();
  }, [normalizarGuardasMasivo, rechazarPorTelefonoMasivo, eliminarRechazadosMasivo, autorizarPendientesMasivo, limpiarUsuariosFantasmaMasivo, normalizarMayusculasTodosMasivo]);

  const botonesAccion = [
    {
      texto: "Opciones",
      accion: abrirOpciones,
      style: { whiteSpace: "nowrap" },
    },
  ];

  return cargando ? (
    <AnimacionCarga />
  ) : (
    <div className="tabla-usuarios-compacta">
      <style>{`
        .tabla-usuarios-compacta .ListadoInmuebles { overflow-x: auto; }
        .tabla-usuarios-compacta .tabla-responsive th,
        .tabla-usuarios-compacta .tabla-responsive td { padding: 4px 5px; }
        .tabla-usuarios-compacta .tabla-responsive { font-size: 0.62rem; table-layout: auto; }
        .tabla-usuarios-compacta .tabla-responsive td:nth-child(1),
        .tabla-usuarios-compacta .tabla-responsive th:nth-child(1) {
          max-width: 160px;
          white-space: normal;
          word-break: break-word;
        }
      `}</style>
      <TablaGenerica
        datos={aplicarFiltrosTarjetas(datos, filtros)}
        titulos={columnas.map((c) => c.label.toUpperCase())}
        campos={columnas.map((c) => c.key)}
        elementosPorPagina={6000}
        columnaOrdenInicial={0}
        direccionOrdenInicial="desc"
        botones={botonesAccion}
        contenidoExtra={
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0", alignItems: "center" }}>
            <FiltrosTarjetas
              datos={datos}
              grupos={[
                { campo: "estadoregistro", etiqueta: "Estado" },
                { campo: "Permisos", etiqueta: "Permiso" },
              ]}
              filtros={filtros}
              setFiltros={setFiltros}
            />
            <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            {false && <button onClick={actualizarCorreosCopropietarios} title="Actualizar Correos"><i className="bi bi-envelope" /></button>}
            <CSVLink data={aplicarFiltrosTarjetas(datos, filtros)} headers={columnas} filename="listado_usuarios.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
            <button onClick={descargarPlantilla} title="Descargar plantilla Excel"><i className="bi bi-file-earmark-excel" /></button>
            <button onClick={abrirSelectorArchivo} title="Cargar usuarios desde Excel"><i className="bi bi-upload" /></button>
            <input
              ref={inputArchivoRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={manejarArchivoSeleccionado}
            />
            <button onClick={() => crearUsuario()} title="Nuevo Usuario"><i className="bi bi-plus-lg" /></button>
            {false && <button onClick={abrirMenuAccionesTemporales} title="Acciones masivas (temporal)"><i className="bi bi-shield-check" /></button>}
            </div>
          </div>
        }
      />
      <BotonSOS />
    </div>
  );
});

export default ListadoUsuarios;